import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@heycater/design-system';
import { ArrowForwardIos } from '@material-ui/icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';

import { useBreakpointUp } from 'qualification/hooks/useBreakpoints';
import Dots from 'shared/components/Dots';
import { usePagination } from 'shared/hooks/usePagination';
import TopCaterersCard from 'static_pages/marketing_pages/components/TopCaterers/TopCaterersCard';
import { buildFunnelUrl } from 'static_pages/marketing_pages/helpers';
import DarkButton from 'system/components/Button/DarkButton';
import TransparentButton from 'system/components/Button/TransparentButton';
import Container from 'system/components/Container';

const PAGINATION = {
  XS: { itemsPerPage: 1, maxPages: 6 },
  SM: { itemsPerPage: 2, maxPages: 6 },
  MD: { itemsPerPage: 3, maxPages: 4 },
  LG: { itemsPerPage: 4, maxPages: 3 },
};

const usePaginationSetup = () => {
  const isSM = useBreakpointUp('sm');
  const isMD = useBreakpointUp('md');
  const isLG = useBreakpointUp('lg');

  let paginationSetup = PAGINATION.XS;

  if (isSM) {
    paginationSetup = PAGINATION.SM;
  }
  if (isMD) {
    paginationSetup = PAGINATION.MD;
  }
  if (isLG) {
    paginationSetup = PAGINATION.LG;
  }

  return paginationSetup;
};

export default function TopCaterers({
  topCaterers = [],
  title,
  city,
  cta,
  cateringCategories,
  ctaLink = undefined,
}) {
  const { t } = useTranslation('static_pages');
  const [isScrolling, setScrolling] = useState(false);
  const stopScrollingStateTimeoutRef = useRef(null);

  const paginationSetup = usePaginationSetup();
  const isDesktop = useBreakpointUp('lg');
  const router = useRouter();

  const cardPageItemsRef = useRef([]);

  useEffect(() => {
    pagination.setPageIndex(0);
  }, [paginationSetup]);

  useEffect(() => {
    return () => {
      if (stopScrollingStateTimeoutRef.current) {
        clearTimeout(stopScrollingStateTimeoutRef.current);
      }
    };
  }, []);

  const handleDotClick = (index) => {
    if (stopScrollingStateTimeoutRef.current) {
      clearTimeout(stopScrollingStateTimeoutRef.current);
    }
    setScrolling(true);
    pagination.setPageIndex(index);
    scrollPageIntoView(index);
    stopScrollingStateTimeoutRef.current = setTimeout(
      () => setScrolling(false),
      800
    );
  };

  const pagination = usePagination(topCaterers, {
    pageSize: paginationSetup.itemsPerPage,
    limit: paginationSetup.itemsPerPage * paginationSetup.maxPages,
  });

  const { inView: isSectionInView, ref: sectionRef } = useInView();

  const scrollPageIntoView = (pageIndex) => {
    const pageElement = cardPageItemsRef?.current?.[pageIndex];

    if (isSectionInView && pageElement) {
      pageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  if (topCaterers.length < 3) {
    return null;
  }

  return (
    <>
      <Container size="md">
        <Box py={6} sm={{ py: 4 }} xs={{ py: 2 }} textAlign="center">
          <TitleTypography component="h2" gutterBottom>
            {title || 'Top Caterers'}
          </TitleTypography>
        </Box>
      </Container>
      {isDesktop ? (
        <CardsWrapper>
          <CardsGrid>
            {pagination.currentPage?.map((caterer) => (
              <TopCaterersCard
                key={caterer.objectID}
                caterer={caterer}
                city={city}
              />
            ))}
          </CardsGrid>

          <SlideButtonWrapper left>
            <SlideButton
              onClick={pagination.prev}
              disabled={!pagination.prevPage}
            >
              <Box mr={0.4} mt={0.4}>
                <ArrowBackIos />
              </Box>
            </SlideButton>
          </SlideButtonWrapper>

          <SlideButtonWrapper right>
            <SlideButton
              onClick={() => handleDotClick(pagination.pageIndex + 1)}
              disabled={!pagination.nextPage}
            >
              <Box ml={0.6} mt={0.4}>
                <ArrowForwardIos />
              </Box>
            </SlideButton>
          </SlideButtonWrapper>
        </CardsWrapper>
      ) : (
        <CardsWrapper ref={sectionRef}>
          {pagination.pages.map((page, index) => (
            <InView
              threshold={1}
              key={index}
              onChange={(inView) => {
                if (inView && !isScrolling) {
                  pagination.setPageIndex(index);
                }
              }}
            >
              <CardsGrid
                ref={(element) => {
                  cardPageItemsRef.current[index] = element;
                }}
              >
                {page.length
                  ? page.map((caterer) => (
                      <TopCaterersCard
                        key={caterer.objectID}
                        caterer={caterer}
                        city={city}
                      />
                    ))
                  : null}
              </CardsGrid>
            </InView>
          ))}
        </CardsWrapper>
      )}

      <Box textAlign="center" mt={{ xs: 2, md: 3 }}>
        <Dots
          count={pagination.pageCount}
          activeIndex={pagination.pageIndex}
          onClick={handleDotClick}
        />
      </Box>
      <Container size="md">
        <Box pt={{ xs: 4, md: 6 }} pb={4} textAlign="center">
          <DarkButton
            size="lg"
            rounded
            as="a"
            target="_blank"
            href={
              ctaLink ||
              buildFunnelUrl({
                city,
                cateringCategories: cateringCategories,
                router,
              })
            }
          >
            {cta ? cta : t('catererCards.cta')}
          </DarkButton>
        </Box>
      </Container>
    </>
  );
}

const TitleTypography = styled(Typography)`
  font-weight: 600;
  font-size: 24px;
  line-height: 150%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 36px;
  }
`;

const SlideButton = styled(TransparentButton)`
  height: 55px;
  width: 55px;
  border-radius: 100px;
  padding: 0;
  display: block;
`;

const ArrowBackIos = styled(ArrowForwardIos)`
  transform: scaleX(-1);
`;

const SlideButtonWrapper = styled.div`
  position: absolute;
  z-index: 3;
  top: 0;
  bottom: 0;
  left: ${({ left }) => (left ? '-60px' : 'none')};
  right: ${({ right }) => (right ? '-60px' : 'none')};
  display: flex;
  align-items: center;
`;

const CardsWrapper = styled.div`
  margin-left: auto;

  scroll-snap-type: x mandatory;
  margin-right: auto;
  position: relative;
  width: fit-content;
  max-width: 100%;
  overflow: auto;
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)}px;
  padding: ${({ theme }) => theme.spacing(0, 2)};
  padding-bottom: ${({ theme }) => theme.spacing(2)}px;

  @media (hover: hover) {
    // No scrollbar on non-touch devices
    overflow: hidden;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-bottom: ${({ theme }) => theme.spacing(3)}px;
    padding: ${({ theme }) => theme.spacing(0, 3)};
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    overflow: visible; // So the slide buttons will be visible
  }
`;

const CardsGrid = styled.div`
  display: grid;
  margin-left: auto;
  position: relative;
  width: fit-content;
  scroll-snap-align: center;

  scroll-behavior: smooth;
  max-width: 100%;
  margin-right: auto;
  display: flex;

  gap: ${({ theme }) => theme.spacing(2)}px;

  align-items: stretch;

  ${({ theme }) => theme.breakpoints.up('md')} {
    gap: ${({ theme }) => theme.spacing(3)}px;
  }
`;
